import * as React from "react";

import { cn } from "@/lib/utils";

export type BadgeVariant = "full" | "opaque" | "outline";
export type BadgeColor =
  | "purple"
  | "blue"
  | "green"
  | "yellow"
  | "red"
  | "grey"
  | "lightGrey";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  color?: BadgeColor;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const baseClasses =
  "inline-flex items-center gap-[2px] rounded-[4px] px-[6px] py-[4px] text-[12px] font-semibold leading-none tracking-[-0.48px]";

const colorMap: Record<
  BadgeColor,
  { full: string; opaque: string; outline: string }
> = {
  purple: {
    full: "bg-sds-purple-100 text-white",
    opaque: "bg-sds-purple-20 text-sds-purple-100",
    outline: "bg-transparent border border-sds-purple-100 text-sds-purple-100",
  },
  blue: {
    full: "bg-sds-blue-100 text-white",
    opaque: "bg-sds-blue-20 text-sds-blue-100",
    outline: "bg-transparent border border-sds-blue-100 text-sds-blue-100",
  },
  green: {
    full: "bg-sds-green-100 text-white",
    opaque: "bg-sds-green-20 text-sds-green-100",
    outline: "bg-transparent border border-sds-green-100 text-sds-green-100",
  },
  yellow: {
    full: "bg-sds-yellow-100 text-[#2c2c2d]",
    opaque: "bg-sds-yellow-20 text-sds-yellow-100",
    outline: "bg-transparent border border-sds-yellow-100 text-sds-yellow-100",
  },
  red: {
    full: "bg-sds-red-100 text-white",
    opaque: "bg-sds-red-20 text-sds-red-100",
    outline: "bg-transparent border border-sds-red-100 text-sds-red-100",
  },
  grey: {
    full: "bg-sds-neutral-80 text-white",
    opaque: "bg-sds-neutral-20 text-sds-neutral-80",
    outline: "bg-transparent border border-sds-neutral-80 text-sds-neutral-80",
  },
  lightGrey: {
    full: "bg-sds-neutral-20 text-sds-neutral-100",
    opaque: "bg-sds-neutral-20 text-sds-neutral-80",
    outline: "bg-transparent border border-sds-neutral-40 text-sds-neutral-80",
  },
};

export function Badge({
  variant = "full",
  color = "purple",
  leftIcon,
  rightIcon,
  className,
  children,
  ...props
}: BadgeProps) {
  const variantClasses = colorMap[color][variant];
  return (
    <span className={cn(baseClasses, variantClasses, className)} {...props}>
      {leftIcon ? (
        <span className="inline-flex size-4 items-center justify-center">
          {leftIcon}
        </span>
      ) : null}
      {children}
      {rightIcon ? (
        <span className="inline-flex size-4 items-center justify-center">
          {rightIcon}
        </span>
      ) : null}
    </span>
  );
}

export default Badge;
